import * as arbitraryEvents from 'js/core_webpack/arbitrary_events';
import { currentUser } from 'js/utils/user';
import { type CoScientistEventName, type EventMetadata } from './events';
import { getAnalyticsSessionId, getChannel, getInstallationId } from './ids';
import { getEnvContext } from './env';
import { getPlanContext } from './planContext';
import { truncatePayload } from './payload';
import { widgetFrameMode } from '../overlay/widgetProtocol';

// bump on breaking envelope changes — keep Electron's ANALYTICS_SCHEMA_VERSION in sync
const SCHEMA_VERSION = 1;

// distinguishes this client from Electron's in the shared Redshift events table
const SURFACE = 'browser';

// toggle via localStorage; no settings UI writes this key yet
const OPT_OUT_KEY = 'coscientist_product_analytics';

// server also drops these via ArbitraryEventController's impersonation backstop
let impersonating = false;

export function setImpersonating(value: boolean): void {
  impersonating = value;
}

// also read by client.ts to suppress the agent server's own analytics during impersonation
export function isImpersonating(): boolean {
  return impersonating;
}

function analyticsEnabled(): boolean {
  if (impersonating) return false;
  // the show action isn't login_required, so track() may run before auth
  if (currentUser == null) return false;
  try {
    if (window.localStorage.getItem(OPT_OUT_KEY) === 'false') return false;
  } catch {
    // storage unavailable — default to enabled
  }
  return true;
}

function withPlan(metadata: EventMetadata): EventMetadata {
  const plan = getPlanContext();
  return plan ? { ...metadata, plan } : metadata;
}

// Every event from a widget's frame says which widget, so its chats separate from the app's
function withWidget(metadata: EventMetadata): EventMetadata {
  const widget = widgetFrameMode();
  return widget ? { widget, ...metadata } : metadata;
}

// return value lets trackFirstOccurrence await confirmation before marking the event sent
export function track(
  eventName: CoScientistEventName,
  metadata: EventMetadata = {},
): void | Promise<Response> {
  if (typeof window === 'undefined') return;
  if (!analyticsEnabled()) return;

  // no user id: the backend attaches actor_id from current_user server-side
  const envelope = {
    v: SCHEMA_VERSION,
    event_name: eventName,
    installation_id: getInstallationId(),
    session_id: getAnalyticsSessionId(),
    channel: getChannel(),
    surface: SURFACE,
    ...getEnvContext(),
    metadata: truncatePayload(withWidget(withPlan(metadata))),
  };

  try {
    return arbitraryEvents.write('CoScientistEvent', envelope);
  } catch {
    return undefined;
  }
}
